/** @jsxImportSource @emotion/react */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AppCallToAction,
  CreateCallToAction,
  CtaAction,
  UpdateCallToAction,
} from "../../../../service/api-response/AppCallToAction";
import { FormProvider, useForm } from "react-hook-form";
import { GoBackLinkButton } from "../../../../components/GoBackLinkButton";
import { routes } from "../../../../appRoutingHelpers";
import { StickyBottomBar } from "../../../../components/StickyBottomBar";
import { PrimaryButton } from "../../../../components/Buttons";
import { colors } from "../../../../components/colors";
import {
  useCreateCta,
  useGetAppCta,
  useGetCountries,
  useUpdateCta,
  useUploadCallToActionImage,
} from "../../../../service/api";
import { SpinnerBlock } from "../../../common/Spinner";
import { toastr } from "react-redux-toastr";
import * as yup from "yup";
import { CtaGeneralInformation } from "./CtaGeneralInformation";
import { CtaImageSection } from "./CtaImageSection";
import { StyledForm } from "../../../../components/StyledForm";
import { PageWrapper } from "../../../../components/page-skeleton/PageWrapper";
import { stickyBottomBarBodyStyle } from "../generic-styles";
import { getCountrySpecificValues } from "../../../../util";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import {
  getErrorMessage,
  useHandleFetchError,
} from "../../../../error-handler";
import { ActionSpinner } from "../../../../components/ActionSpinner";

const ctaSchema = yup.object().shape({
  title: yup.string().required("Please enter title to continue"),
  action: yup.string().required("Please choose the CTA type to continue"),
  countryCode: yup
    .string()
    .required("Please choose the country code of the CTA to continue"),
  imageUrl: yup.string(),
  description: yup.string().required("Please enter description to continue"),
  files: yup
    .array()
    .of(yup.mixed())
    .when("imageUrl", {
      is: (imageUrl: string) => !imageUrl,
      then: (schema) => schema.min(1, "Please upload an image to continue"),
      otherwise: (schema) => schema,
    }),
  locale: yup
    .string()
    .required("Please choose the locale of the CTA to continue"),
  externalUrl: yup.string().when("action", {
    is: (val: CtaAction) => val === "external",
    then: (schema) => schema.required("External link is required"),
    otherwise: (schema) => schema,
  }),
  sortKey: yup
    .number()
    .typeError("Sort key must be a number.")
    .required("Please enter sort key to continue."),
});

export const CtasUpsert = () => {
  const { countryCode } = getCountrySpecificValues();
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useForm<AppCallToAction>({
    resolver: yupResolver(ctaSchema),
    defaultValues: {
      action: "discount_card",
      countryCode: countryCode,
      files: [],
      locale: countryCode.toLowerCase(),
      externalUrl: "",
    },
  });
  const {
    data: cta,
    isLoading: fetchIsLoading,
    error: getCtaError,
  } = useGetAppCta(organizationKeyName, id);

  const {
    data: countries,
    isLoading: countriesAreLoading,
    error: getCountriesError,
  } = useGetCountries();

  const { mutateAsync: createCTA } = useCreateCta(organizationKeyName);
  const { mutateAsync: updateCTA } = useUpdateCta(organizationKeyName);
  const { mutateAsync: uploadImage } = useUploadCallToActionImage();

  const navigate = useNavigate();
  const { reset, handleSubmit } = methods;
  const isEdit = !!id;

  useEffect(() => {
    if (isEdit && cta) {
      reset({
        ...cta,
        files: [],
      });
    }
  }, [cta, reset, isEdit]);

  const onSubmit = async (cta: AppCallToAction) => {
    const { imageUrl, files, ...rest } = cta;
    let imageUrlToUse = imageUrl || "";
    let ctaId = id || "";
    try {
      if (!isEdit) {
        const request: CreateCallToAction = {
          data: {
            ...rest,
            externalUrl: cta.action === "external" ? cta.externalUrl : null,
          },
          organizationKeyName,
        };
        ctaId = await createCTA(request);
      }
      if (files?.length > 0) {
        const url = await uploadImage({
          file: files[0],
          id: ctaId,
          organizationKeyName,
        });
        imageUrlToUse = url;
      }
      const updatePayload: UpdateCallToAction = {
        data: {
          imageUrl: imageUrlToUse,
          ...rest,
          externalUrl: cta.action === "external" ? cta.externalUrl : null,
        },
        id: ctaId,
        organizationKeyName,
      };
      await updateCTA(updatePayload);

      toastr.success(
        "Success",
        `You succesfully ${isEdit ? "updated" : "created"} the call to action`
      );
      navigate(routes.appHome(organizationKeyName) + `?key=2`);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };
  const buttonText = isEdit ? "Save changes" : "Create call to action";

  useHandleFetchError(getCountriesError);
  useHandleFetchError(getCtaError);
  if (fetchIsLoading || countriesAreLoading) {
    return <SpinnerBlock />;
  }

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <StyledForm>
        <PageWrapper>
          <GoBackLinkButton
            to={routes.appHome(organizationKeyName) + `?key=2`}
            text="Back to all call to actions"
          />
          <CtaGeneralInformation countries={countries || []} />
          <CtaImageSection />
        </PageWrapper>
        <StickyBottomBar>
          <div css={stickyBottomBarBodyStyle}>
            <PrimaryButton
              color="white"
              backgroundColor={colors.ctaMain}
              hoverColor={colors.ctaSecondaryOne}
              type="button"
              disabled={methods.formState.isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {methods.formState.isSubmitting ? <ActionSpinner /> : buttonText}
            </PrimaryButton>
          </div>
        </StickyBottomBar>
      </StyledForm>
    </FormProvider>
  );
};
